/**
 * @fileoverview Conversar com a IA como se fosse um cliente, antes de ativá-la
 * (a conversa de teste aceita o agente desligado; nada daqui vai para um
 * cliente). Começa pela saudação dela e pelas perguntas tiradas da base, sem
 * uma área vazia; a pergunta escolhida no passo 3 já vai respondendo ao abrir.
 * Depois da primeira resposta, um retorno discreto; viu algo errado, o atalho
 * leva à base para corrigir.
 */

import React, { useEffect, useRef, useState } from "react";
import { Dialog, IconButton } from "@material-ui/core";
import { CheckCircle2, RotateCcw, Send, X } from "lucide-react";

import { i18n } from "../../translate/i18n";
import { useAuthFormStyles } from "../AuthLayout/formStyles";
import useTestChat from "../../hooks/useTestChat";
import { initialsOf, isDefaultName } from "./LearnedStep";
import { useAiSetupStyles } from "./styles";

const TestChatDialog = ({ open, onClose, agentId, versionId, agentName, initialQuestion, greeting, suggestions, onFixBase }) => {
  const classes = useAiSetupStyles();
  const form = useAuthFormStyles();
  const { messages, loading, send, clear } = useTestChat(agentId, { errorText: i18n.t("aiSetup.test.error"), versionId });
  const [input, setInput] = useState("");
  const endRef = useRef(null);
  const inputRef = useRef(null);

  // Cada abertura é uma conversa nova; a pergunta escolhida já vai.
  useEffect(() => {
    if (!open) return;
    clear();
    setInput("");
    if (initialQuestion) send(initialQuestion);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (endRef.current) endRef.current.scrollIntoView({ block: "end", behavior: "smooth" });
  }, [messages, loading]);

  const submit = (text) => {
    if (!text.trim() || loading) return;
    setInput("");
    send(text);
  };

  const firstAnswer = messages.findIndex((message) => message.role === "assistant" && !message.isError);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{ className: classes.dialogPaper }}
      aria-labelledby="ai-test-title"
      TransitionProps={{ onEntered: () => !initialQuestion && inputRef.current?.focus() }}
    >
      <div className={classes.dialogHead}>
        <div>
          <h2 id="ai-test-title" className={classes.dialogTitle}>
            {isDefaultName(agentName) ? i18n.t("aiSetup.test.titleDefault") : i18n.t("aiSetup.test.title", { name: agentName })}
          </h2>
          <p className={classes.dialogText}>{i18n.t("aiSetup.test.subtitle")}</p>
        </div>
        <div>
          {messages.length > 0 && (
            <IconButton size="small" onClick={clear} className={classes.dialogClose} aria-label={i18n.t("aiSetup.test.restart")} title={i18n.t("aiSetup.test.restart")}>
              <RotateCcw size={17} />
            </IconButton>
          )}
          <IconButton size="small" onClick={onClose} className={classes.dialogClose} aria-label={i18n.t("aiSetup.close")}>
            <X size={18} />
          </IconButton>
        </div>
      </div>
      <div className={classes.chatBody}>
        <div className={classes.chatScroll} aria-live="polite">
          <div className={classes.chatGreetingRow}>
            <span className={classes.aiAvatar} aria-hidden="true">{initialsOf(agentName)}</span>
            <div className={`${classes.bubble} ${classes.fromAi}`} style={{ maxWidth: "none", animation: "none" }}>{greeting}</div>
          </div>
          {messages.length === 0 && !loading && (
            <div className={classes.chatSuggestions}>
              {(suggestions || []).map((question) => (
                <button key={question} type="button" className={classes.chip} onClick={() => submit(question)}>
                  {question}
                </button>
              ))}
            </div>
          )}
          {messages.map((message, index) => (
            <React.Fragment key={message.id}>
              <div className={`${classes.bubble} ${message.role === "user" ? classes.fromCustomer : classes.fromAi}${message.isError ? ` ${classes.bubbleError}` : ""}`}>
                {message.content}
              </div>
              {index === firstAnswer && (
                <div className={classes.praise} role="status">
                  <CheckCircle2 size={15} /> {i18n.t("aiSetup.test.praise")}
                </div>
              )}
            </React.Fragment>
          ))}
          {loading && (
            <div className={classes.thinking}>
              {isDefaultName(agentName) ? i18n.t("aiSetup.test.thinkingDefault") : i18n.t("aiSetup.test.thinking", { name: agentName })}
            </div>
          )}
          <div ref={endRef} />
        </div>
        <form
          className={classes.chatInputRow}
          onSubmit={(event) => {
            event.preventDefault();
            submit(input);
          }}
        >
          <input
            ref={inputRef}
            className={form.input}
            value={input}
            onChange={(event) => setInput(event.target.value)}
            placeholder={i18n.t("aiSetup.test.placeholder")}
            aria-label={i18n.t("aiSetup.test.placeholder")}
            disabled={loading}
          />
          <button type="submit" className={`${form.button} ${classes.sendButton}`} disabled={loading || !input.trim()} aria-label={i18n.t("aiSetup.test.send")}>
            <Send size={17} />
          </button>
        </form>
        {onFixBase && (
          <div className={classes.chatFoot}>
            <span>{i18n.t("aiSetup.test.wrong")}</span>
            <button type="button" className={form.link} onClick={onFixBase}>{i18n.t("aiSetup.test.fix")}</button>
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default TestChatDialog;
